import React from 'react';
import { Anchor, Box, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import type { TypeFilterSection } from '@/types/list/types.list.filter';
import FilterOption from './Filter.Option';

type Props = {
    section: TypeFilterSection;
    selected: string[];
    excluded: boolean;
    onToggle: (value: string) => void;
    onExclude?: (value: boolean) => void;
};

const SEARCH_FROM = 12;
const FOLDED = 8;

const FilterSection: React.FC<Props> = ({ section, selected, excluded, onToggle, onExclude }) => {
    const [term, setTerm] = React.useState('');
    const [expanded, setExpanded] = React.useState(false);
    const searchable = section.searchable ?? section.options.length >= SEARCH_FROM;
    const needle = term.trim().toLowerCase();

    const matching = needle ? section.options.filter((option) => option.label.toLowerCase().includes(needle)) : section.options;
    const folded = !needle && !expanded && matching.length > FOLDED + 2;
    const visible = folded
        ? [...matching.filter((option) => selected.includes(option.value)), ...matching.filter((option) => !selected.includes(option.value))].slice(0, Math.max(FOLDED, selected.length))
        : matching;

    return (
        <Box className="bd-fp-section" role="group" aria-label={section.label}>
            {section.negatable && onExclude ? (
                <SegmentedControl
                    size="xs"
                    fullWidth
                    mb="xs"
                    value={excluded ? 'not' : 'is'}
                    onChange={(value) => onExclude(value === 'not')}
                    data={[{ value: 'is', label: 'È' }, { value: 'not', label: 'Non è' }]}
                    aria-label={`Come usare i valori di ${section.label}`}
                />
            ) : null}

            {searchable ? (
                <TextInput
                    size="xs"
                    mb={6}
                    value={term}
                    onChange={(event) => setTerm(event.currentTarget.value)}
                    onKeyDown={(event) => { if (event.key !== 'Escape') event.stopPropagation(); }}
                    placeholder={`Cerca in ${section.label.toLowerCase()}`}
                    leftSection={<IconSearch size={13} stroke={1.8} />}
                    aria-label={`Cerca fra i valori di ${section.label}`}
                    data-autofocus
                />
            ) : null}

            {visible.length === 0 ? (
                <Text size="xs" c="dimmed" py={6}>{needle ? `Nessun valore con “${term.trim()}”` : 'Nessun valore da scegliere'}</Text>
            ) : visible.map((option) => (
                <FilterOption
                    key={option.value}
                    option={option}
                    single={section.single}
                    checked={selected.includes(option.value)}
                    onToggle={() => onToggle(option.value)}
                />
            ))}

            {folded ? (
                <Anchor component="button" type="button" size="xs" mt={4} onClick={() => setExpanded(true)}>
                    Mostra tutti ({matching.length})
                </Anchor>
            ) : null}
        </Box>
    );
};

export default FilterSection;
